import type { CollaboratorAllocation } from '@/features/Collaborators/Models';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import { isActiveOnDate, isAbsence } from '@/features/Collaborators/Allocations/utils/allocationDates';

const getDate = (value?: any | null): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export function selectCurrentManagedAllocations(
  allocations: CollaboratorAllocation[],
  today: Date
): CollaboratorAllocation[] {
  return allocations.filter(
    (alloc) =>
      alloc.status !== 'Cancelled' &&
      !isAbsence(alloc) &&
      alloc.responsibleId &&
      isActiveOnDate(alloc, today)
  );
}

export interface ManagerSuggestionWithMeta {
  collaborator: CollaboratorPublic;
  source: 'allocation' | 'registry';
  hint?: string;
}

export function buildManagerSuggestions(options: {
  allocationResponsibles: Array<{ allocation: CollaboratorAllocation; responsible: CollaboratorPublic | null }>;
  registryResponsible: CollaboratorPublic | null | undefined;
  excludeId?: string | null | undefined;
}): ManagerSuggestionWithMeta[] {
  const { allocationResponsibles, registryResponsible, excludeId } = options;
  const seen = new Set<string>();
  const result: ManagerSuggestionWithMeta[] = [];
  const excludeIdNormalized = excludeId ?? undefined;

  const sorted = [...allocationResponsibles].sort((a, b) => {
    const startA = getDate(a.allocation.startDate)?.getTime() ?? 0;
    const startB = getDate(b.allocation.startDate)?.getTime() ?? 0;
    return startB - startA;
  });

  for (const { allocation, responsible } of sorted) {
    if (!responsible || seen.has(responsible.id) || responsible.id === excludeIdNormalized) continue;

    seen.add(responsible.id);
    const hint: string | undefined =
      allocation.clientName && allocation.projectName
        ? `${allocation.clientName} · ${allocation.projectName}`
        : allocation.clientName || allocation.projectName || undefined;

    result.push({
      collaborator: responsible,
      source: 'allocation',
      hint,
    });
  }

  if (result.length === 0 && registryResponsible && !seen.has(registryResponsible.id) && registryResponsible.id !== excludeIdNormalized) {
    result.push({
      collaborator: registryResponsible,
      source: 'registry',
    });
  }

  return result;
}
